<script setup lang="ts">
import { h,defineProps,reactive,Ref,computed,ref, watch,onMounted,inject,watchEffect } from 'vue'
import axios from 'axios';
import { useRouter, useRoute } from 'vue-router'
import {PhotoIcon,TrophyIcon,UserGroupIcon,AdjustmentsHorizontalIcon,CloudArrowUpIcon,SparklesIcon,Cog8ToothIcon,BellAlertIcon,ChevronDoubleLeftIcon}  from "@heroicons/vue/24/solid";
// SHADCN
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, } from '@/components/ui/sheet'
import { CalendarDate, DateFormatter, getLocalTimeZone, today } from '@internationalized/date'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue, } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { toast as sonnerToast } from 'vue-sonner'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { VhostInfo,VhostSettings,defaultVhostInfo  } from '@/types/Vhost'
import vhost from '@/services/Vhost'
import UploaderConfig from '@/components/UploaderConfig.vue'
import CustomMetaData from '@/components/CustomMetaData.vue'
import WebsiteDisplay from '@/components/WebsiteDisplay.vue'
import FormSelectIcon from 'vue-material-design-icons/FormSelect.vue';
const route = useRoute();
const router = useRouter();
interface MenuItem {
	title: string
	value: string
	icon?: any
	submenu?: MenuItem[]; 
	hide?: boolean;
}
const currentMenu = ref<"main" | "submenu">("main");
const subMenuItems = ref<{ title: string; value: string }[] | null>(null);
const transitionDirection = ref('enter-left-to-right');
const parentTitle=ref('');
const url=ref('');
const step=ref(0);
const loaded=ref(false);
const currentSection = computed(() => {
  // route.name will be 'SettingsGeneral', 'SettingsMedia', etc.
  // We extract the part after 'Settings'
  if (!route.name) return 'general'
  return (route.name as string).replace('Settings', '').toLowerCase()
})
const capitalizeFirstLetter = (string: string): string => { return string.charAt(0).toUpperCase() + string.slice(1) }
const navigateTo = (item: MenuItem) => {
  if (item.submenu) {
    subMenuItems.value = item.submenu
  } else {
    // Use the route name we defined in the router
    router.push({ name: `Settings${capitalizeFirstLetter(item.value)}` })
  }
}

// Handle menu selection
const goBack = () => {
	currentMenu.value = "main";
	subMenuItems.value = null;
};

// Computed menu items
//const menuItems = computed<MenuItem[]>(() =>
//  currentMenu.value === "main" ? sidebarNavItems.value : subMenuItems.value || []
//);

const updatedInfo = ref<VhostInfo>(defaultVhostInfo);
const vhostInfo = inject<Ref<VhostInfo> | null>('vhostInfo', null);
const vhostId = inject<Ref<string> | null>('vhostId', null);
if (vhostInfo) {
	if(vhostInfo.value) {
		updatedInfo.value=vhostInfo.value;
	}
}
// Reactively update when vhostInfo loads/changes (handles async timing)
watch(() => vhostInfo?.value, (newVal) => {
	if (newVal) {
		updatedInfo.value = newVal;
	}
}, { immediate: true });
const df = new DateFormatter('en-US', {
	dateStyle: 'long',
})

const accountFormSchema = toTypedSchema(z.object({
	name: z
	.string({
		required_error: 'Required.',
	})
	.min(2, {
		message: 'Name must be at least 2 characters.',
	})
	.max(30, {
		message: 'Name must not be longer than 30 characters.',
	}),
	dob: z.string().datetime().optional().refine(date => date !== undefined, 'Please select a valid date.'),
	language: z.string().min(1, 'Please select a language.'),
}))

// https://github.com/logaretm/vee-validate/issues/3521
// https://github.com/logaretm/vee-validate/discussions/3571
async function onSubmit(values: any) {
	toast({
		title: 'You submitted the following values:',
		description: h('pre', { class: 'mt-2 w-[340px] rounded-md bg-slate-950 p-4' }, h('code', { class: 'text-white' }, JSON.stringify(values, null, 2))),
	})
}




const tab = ref(0);
const selectedPage = ref('general');
const selectedSubPage = ref('up-defaults');
const sidebarNavItems: MenuItem[] = [
	{
		title: "General",
		value: "general",
		icon: AdjustmentsHorizontalIcon,
		hide: false,
	},
	{
		title: "Meta Data",
		value: "metadata",
		icon: FormSelectIcon,
		hide: false,
	},
	{
		title: "Uploader",
		value: "uploader",
		icon: CloudArrowUpIcon,
		hide: !vhostInfo?.value?.uploaderId,
		submenu: [
			{ title: "Configuration", value: "up-defaults" }, // 🔄 Changed 'label' to 'title'
			{ title: "Login", value: "up-login" },
			{ title: "Registration", value: "up-reg" },
			{ title: "Upload Settings", value: "up-set" },
			{ title: "Upload Form", value: "up-form" },
			{ title: "Look and Feel", value: "up-feel" },
			{ title: "SSO", value: "up-sso" },
			{ title: "Mobile", value: "up-mobile" },
		],
	},
	{
		title: "Media",
		value: "media",
		icon: PhotoIcon,
		hide: false,
	},
	{
		title: "Users",
		value: "users",
		icon: UserGroupIcon,
		hide: false,
	},
	{
		title: "Gamification",
		value: "game",
		icon: TrophyIcon,
		hide: true,
	},
	{
		title: "AI",
		value: "ai",
		icon: SparklesIcon,
		hide: false,
	},
	{
		title: "Notifications",
		value: "notifications",
		icon: BellAlertIcon,
		hide: false,
	},
	{
		title: "Advanced",
		value: "dev",
		icon: Cog8ToothIcon,
		hide: false,
	},
];

// General + AI Settings save
const { updateProjectSettings } = vhost()
const savingGeneral = ref(false)
const savingAI = ref(false)

const saveGeneralSettings = async () => {
	if (!vhostId?.value || !updatedInfo.value) return
	savingGeneral.value = true
	try {
		const result = await updateProjectSettings(Number(vhostId.value), {
			name: updatedInfo.value.name,
			contactemail: updatedInfo.value.contactemail,
			lightLogo: updatedInfo.value.settings?.lightLogo ?? '',
			slackUrl: updatedInfo.value.settings?.slackUrl ?? '',
		})
		if (result) {
			sonnerToast.success('General settings saved')
		} else {
			sonnerToast.error('Failed to save general settings')
		}
	} catch (err) {
		sonnerToast.error('Error saving general settings')
	} finally {
		savingGeneral.value = false
	}
}

const saveMediaSettings = async () => {
	if (!vhostId?.value || !updatedInfo.value) return
	savingGeneral.value = true
	try {
		const result = await updateProjectSettings(Number(vhostId.value), {
			storagedomain: updatedInfo.value.storagedomain ?? '',
		})
		if (result) {
			sonnerToast.success('Media settings saved')
		} else {
			sonnerToast.error('Failed to save media settings')
		}
	} catch (err) {
		sonnerToast.error('Error saving media settings')
	} finally {
		savingGeneral.value = false
	}
}

const saveAISettings = async () => {
	if (!vhostId?.value || !updatedInfo.value) return
	savingAI.value = true
	try {
		const s = updatedInfo.value.settings
		const result = await updateProjectSettings(Number(vhostId.value), {
			enable_fake_detection: s.enable_fake_detection ?? 0,
			fake_detection_threshold: s.fake_detection_threshold ?? 60,
			fake_detection_automod: s.fake_detection_automod ?? 0,
			enable_aws_deep_analysis: s.enable_aws_deep_analysis ?? 0,
			enable_selfhosted_ml: s.enable_selfhosted_ml ?? 0,
			selfhosted_ml_url: s.selfhosted_ml_url ?? '',
		})
		if (result) {
			sonnerToast.success('AI settings saved')
		} else {
			sonnerToast.error('Failed to save AI settings')
		}
	} catch (err) {
		sonnerToast.error('Error saving AI settings')
	} finally {
		savingAI.value = false
	}
}

</script>
<template>
	<div class="flex-1 space-y-4 ">
		<div class="w-full flex justify-start items-start flex-col lg:flex-row lg:space-y-0 " >
			<!-- Mobile section picker -->
			<div class="block lg:hidden px-2 pb-2">
				<Select :model-value="currentSection" @update:model-value="(val: string) => navigateTo(sidebarNavItems.find(i => i.value === val) || sidebarNavItems[0])">
					<SelectTrigger>
					<SelectValue placeholder="Select a section" />
					</SelectTrigger>
					<SelectContent>
					<SelectGroup>
					<template v-for="(item,index) in sidebarNavItems" :key="index">
						<SelectItem v-if="!item.hide" :value="item.value">{{ item.title }}</SelectItem>
					</template>
					</SelectGroup>
					</SelectContent>
				</Select>
			</div>

			<!-- Desktop sidebar -->
			<aside class="hidden lg:block w-[200px] shrink-0 py-2 pl-4">
				<nav class="flex flex-col gap-0.5">
					<template v-for="(item, idx) in sidebarNavItems" :key="idx">
						<button v-if="!item.hide"
							@click="navigateTo(item)"
							class="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted"
							:class="currentSection === item.value ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground'"
						>
							<component :is="item.icon" v-if="item.icon" class="h-4 w-4" />
							{{ item.title }}
						</button>
					</template>
				</nav>
			</aside>
			<Transition mode="out-in" name="slide-fade" > 
			<div v-if=" currentSection=='media'" class="grow w-full md:px-5 px-2">
				<div class="flex items-center justify-between mb-2">
					<h2 class="text-2xl tracking-tight">Storage</h2>
					<Button @click="saveMediaSettings" :disabled="savingGeneral">
						{{ savingGeneral ? 'Saving...' : 'Save Changes' }}
					</Button>
				</div>
				<Separator class="my-6" />
				<h2 class="text-2xl tracking-tight mt-3">Hosting</h2>
				<div class="grid grid-cols-1 lg:grid-cols-2 gap-10 w-full ml-3">
					<div>
						<FormField v-slot="{ componentField }" name="storagedomain" v-if="typeof updatedInfo !='undefined'" class="mt-3">
						<FormItem>
						<FormLabel>Content delivery network URL</FormLabel>
						<FormControl>
						<Input type="text" placeholder="CDN URL" v-model="updatedInfo.storagedomain" />
						</FormControl>
						<FormDescription>
						The url of your CDN, ensure to use storage.newspark.ca as your origin server.
						</FormDescription>
						<FormMessage />
						</FormItem>
						</FormField>
					</div>
					<div>
					</div>
				</div>
				<Separator class="my-6" />
				<h2 class="text-2xl tracking-tight mt-3 ">Lifecycle</h2>
				<p class="text-muted text-muted-foreground">Settings to control which media should be deleted, and what type frame</p>
				<div class="grid grid-cols-1 lg:grid-cols-2 gap-10 w-full ml-3 mt-4">
					<div>
						<label>Content to Delete</label>
						<Select>
							<SelectTrigger>
							<SelectValue placeholder="Moderation Status of Items" />
							</SelectTrigger>
							<SelectContent>
							<SelectGroup>
							<SelectItem value="0"> None </SelectItem>
							<SelectItem value="1"> Any </SelectItem>
							<SelectItem value="2"> Trashed </SelectItem>
							<SelectItem value="3"> Deleted </SelectItem>
							<SelectItem value="4"> Denied </SelectItem>
							</SelectGroup>
							</SelectContent>
						</Select>
					</div>
					<div>
						<label>Schedule</label>
						<Select>
							<SelectTrigger>
							<SelectValue placeholder="Age of content to delete." />
							</SelectTrigger>
							<SelectContent>
							<SelectGroup>
							<SelectItem value="0"> Never </SelectItem>
							<SelectItem value="7"> 7 Days </SelectItem>
							<SelectItem value="30"> 30 Days </SelectItem>
							<SelectItem value="60"> 60 Days </SelectItem>
							<SelectItem value="90"> 90 Days </SelectItem>
							<SelectItem value="180"> 6 Months </SelectItem>
							<SelectItem value="365"> 1 year </SelectItem>
							<SelectItem value="730"> 2 Years </SelectItem>
							<SelectItem value="1825"> 5 Years </SelectItem>
							</SelectGroup>
							</SelectContent>
						</Select>
					</div>
				</div>
			</div>
			<div v-if=" currentSection=='uploader' && vhostInfo &&  vhostInfo.uploaderId && vhostId " class="grow w-full flex flex-col justify-start md:px-5 px-2">
				<div class="flex flex-row justify-between">
					<div>
						<h2 class="text-2xl  tracking-tight">Uploader Configuration</h2>
					</div>
					<div>
						<Sheet>
						<SheetTrigger> <Button>Preview</Button>
						</SheetTrigger>
						<SheetContent  style="max-width: 50vw">
						<SheetHeader>
						<SheetTitle> Preview</SheetTitle>
						<SheetDescription>
						<WebsiteDisplay v-if="vhostInfo.id" :url="'https://'+vhostInfo.id+'.uploader.newspark.ca/'" :refresh="step"/>
						</SheetDescription>
						</SheetHeader>
						</SheetContent>
						</Sheet>

					</div>
				</div>
				<div>
				<UploaderConfig :page="selectedSubPage" :appId="vhostInfo.uploaderId" :vhostId="vhostId"/>
				</div>

			</div>
			<div v-if=" currentSection=='metadata'" class="grow w-full md:px-5 px-2">
				<CustomMetaData/>
			</div>
			<div v-if="currentSection=='ai'" class="grow w-full md:px-5 px-2">
				<div class="flex justify-between items-center">
					<div>
						<h2 class="text-2xl tracking-tight">Content Authenticity</h2>
						<p class="text-sm text-muted-foreground mt-1">Analyze uploaded media for signs of AI generation or manipulation.</p>
					</div>
					<Button @click="saveAISettings" :disabled="savingAI">
						{{ savingAI ? 'Saving...' : 'Save Changes' }}
					</Button>
				</div>
				<Separator class="my-6" />
				<div class="space-y-6 ml-3">
					<div class="flex items-center justify-between max-w-2xl">
						<div>
							<p class="font-medium">Enable Content Authenticity Detection</p>
							<p class="text-sm text-muted-foreground">Runs local metadata forensics, dimension analysis, JPEG quantization checks, and C2PA content credential verification on all uploaded media.</p>
						</div>
						<Switch :checked="updatedInfo.settings.enable_fake_detection == 1" @update:checked="updatedInfo.settings.enable_fake_detection = $event ? 1 : 0" />
					</div>
					<div v-if="updatedInfo.settings.enable_fake_detection == 1" class="space-y-6 ml-4 border-l-2 border-muted pl-6">
						<div class="flex items-center justify-between max-w-2xl">
							<div>
								<p class="font-medium">Auto-deny AI-generated Content</p>
								<p class="text-sm text-muted-foreground">Automatically deny media that exceeds the authenticity threshold. Content will still be flagged regardless.</p>
							</div>
							<Switch :checked="updatedInfo.settings.fake_detection_automod == 1" @update:checked="updatedInfo.settings.fake_detection_automod = $event ? 1 : 0" />
						</div>
						<div v-if="updatedInfo.settings.fake_detection_automod == 1" class="max-w-md">
							<label class="text-sm font-medium">Auto-deny Threshold: {{ updatedInfo.settings.fake_detection_threshold || 60 }}%</label>
							<input type="range" class="w-full mt-2 accent-primary" :value="updatedInfo.settings.fake_detection_threshold || 60" @input="updatedInfo.settings.fake_detection_threshold = Number(($event.target as HTMLInputElement).value)" min="10" max="95" step="5" />
							<p class="text-xs text-muted-foreground mt-1">Media above this threshold will be automatically denied. Lower = more aggressive. Recommended: 60%.</p>
						</div>
						<Separator />
						<div class="flex items-center justify-between max-w-2xl">
							<div>
								<p class="font-medium">Enable AWS Deep Analysis</p>
								<p class="text-sm text-muted-foreground">Uses AWS Rekognition for additional detection signals. Incurs standard AWS API charges.</p>
							</div>
							<Switch :checked="updatedInfo.settings.enable_aws_deep_analysis == 1" @update:checked="updatedInfo.settings.enable_aws_deep_analysis = $event ? 1 : 0" />
						</div>
						<Separator />
						<div class="flex items-center justify-between max-w-2xl">
							<div>
								<p class="font-medium">Enable Self-Hosted ML Detector</p>
								<p class="text-sm text-muted-foreground">Runs multi-model ML detector (CLIP + ResNet + frequency analysis) via Docker. No external API costs.</p>
							</div>
							<Switch :checked="updatedInfo.settings.enable_selfhosted_ml == 1" @update:checked="updatedInfo.settings.enable_selfhosted_ml = $event ? 1 : 0" />
						</div>
						<div v-if="updatedInfo.settings.enable_selfhosted_ml == 1" class="max-w-md ml-4">
							<FormField v-slot="{ componentField }" name="selfhosted_ml_url">
								<FormItem>
									<FormLabel>ML Detector URL</FormLabel>
									<FormControl>
										<Input type="text" placeholder="http://127.0.0.1:8100" v-model="updatedInfo.settings.selfhosted_ml_url" />
									</FormControl>
									<FormDescription>Default: http://127.0.0.1:8100 — change if running on a different host/port.</FormDescription>
								</FormItem>
							</FormField>
						</div>
					</div>
				</div>
			</div>
			<div v-if=" currentSection=='general'" class="grow w-full md:px-4 px-2">
				<Form v-slot="{ setFieldValue }" :validation-schema="accountFormSchema" class="" @submit="onSubmit">
					<div class="flex items-center justify-between mb-2">
						<h2 class="text-lg tracking-tight">Information</h2>
						<Button @click="saveGeneralSettings" :disabled="savingGeneral">
							{{ savingGeneral ? 'Saving...' : 'Save Changes' }}
						</Button>
					</div>
					<div class="grid grid-cols-1 lg:grid-cols-2 gap-10 w-full ml-3 mt-2">
						<div>
							<FormField v-slot="{ componentField }" name="name" v-if="typeof updatedInfo !='undefined'" class="mt-3">
							<FormItem>
							<FormLabel>Project Name</FormLabel>
							<FormControl>
							<Input type="text" placeholder="Project name" v-model="updatedInfo.name" />
							</FormControl>
							<FormDescription>
							This is the name that will be displayed to identify your project.
							</FormDescription>
							<FormMessage />
							</FormItem> 
							</FormField>
						</div>
						<div>
							<FormField v-slot="{ componentField }" name="logorul" v-if="typeof updatedInfo !='undefined'" class="mt-3">
							<FormItem>
							<FormLabel>Logo URL</FormLabel>
							<FormControl>
							<Input type="text" placeholder="Logo URL" v-model="updatedInfo.settings.lightLogo" />
							</FormControl>
							<FormDescription>
							URL to custom logo, to display to users of your project.
							</FormDescription>
							<FormMessage />
							</FormItem>
							</FormField>
						</div>
					</div>
					<Separator class="my-6" />
					<h2 class="text-lg  tracking-tight mt-3">Contact Defails</h2>
					<div class="grid grid-cols-1 lg:grid-cols-2 gap-10 w-full ml-3 mt-2">
						<div>
							<FormField v-slot="{ componentField }" name="contactemail" v-if="typeof updatedInfo !='undefined'" class="mt-3">
							<FormItem>
							<FormLabel>Contact E-Mail</FormLabel>
							<FormControl>
							<Input type="email" placeholder="Contact E-Mail" v-model="updatedInfo.contactemail" />
							</FormControl>
							<FormDescription>
							This address will be used to send relevant information to.
							</FormDescription>
							<FormMessage />
							</FormItem>
							</FormField>
						</div>
						<div>
							<FormField v-slot="{ componentField }" name="slackurl" v-if="typeof updatedInfo !='undefined'" class="mt-3">
							<FormItem>
							<FormLabel>Slack URL</FormLabel>
							<FormControl>
							<Input type="email" placeholder="Slack URL" v-model="updatedInfo.settings.slackUrl"/>
							</FormControl>
							<FormDescription>
							URL to send Slack notifications to, allowing you to get real time updates on platform activity.
							</FormDescription>
							<FormMessage />
							</FormItem>
							</FormField>
						</div>
					</div>


				</Form>

			</div>
			<div v-if="currentSection=='text'">
				<div class="w-full">
					<Tabs class="space-y-4" v-model="tab" :default-value="0">
					<TabsList>
					<TabsTrigger :value="0">
					General
					</TabsTrigger>
					<TabsTrigger :value="1" >
					Media
					</TabsTrigger>
					<TabsTrigger :value="2" >
					Members
					</TabsTrigger>
					<TabsTrigger :value="3" >
					Gamification
					</TabsTrigger>
					<TabsTrigger :value="4" >
					AI
					</TabsTrigger>
					<TabsTrigger :value="5" >
					Notifications
					</TabsTrigger>
					<TabsTrigger :value="6" >
					Developers
					</TabsTrigger>
					</TabsList>
					</Tabs>

				</div>
			</div>
			</Transition>
		</div>
	</div>

</template>
<style>
.v-enter-active,
.v-leave-active {
	transition: opacity 1s ease;
}
.v-enter-from,
.v-leave-to {
	opacity:0
}
.container {
	cursor: pointer;
	position: relative;
	width: 100%;
	height: 100%;
}

.list-container {
	padding: 20px;
	background-color: lightgray;
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
}

/* Tailwind-based transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
	transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;
}

.slide-fade-enter, .slide-fade-leave-to {
	opacity: 0;
}

.slide-fade-enter-left-to-right {
	transform: translateX(100%);
}

.slide-fade-enter-right-to-left {
	transform: translateX(-100%);
}

.slide-fade-leave-left-to-right {
	transform: translateX(-100%);
}

.slide-fade-leave-right-to-left {
	transform: translateX(100%);
}
</style>
